const STORAGE_KEY = 'v4_recent_jobs';
const MAX_ENTRIES = 25;

export interface RecentJobEntry {
  id: number;
  jobId: string;
  client: string;
  location: string;
  accessedAt: number;
}

const isValidEntry = (e: unknown): e is RecentJobEntry =>
  typeof e === 'object' && e !== null &&
  typeof (e as RecentJobEntry).id === 'number' &&
  typeof (e as RecentJobEntry).jobId === 'string' &&
  typeof (e as RecentJobEntry).accessedAt === 'number';

const readEntries = (): RecentJobEntry[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidEntry);
  } catch {
    return [];
  }
};

const writeEntries = (entries: RecentJobEntry[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(0, MAX_ENTRIES)));
};

export const trackJobAccess = (entry: Omit<RecentJobEntry, 'accessedAt'>) => {
  const entries = readEntries().filter((e) => e.id !== entry.id);
  entries.unshift({ ...entry, accessedAt: Date.now() });
  writeEntries(entries);
};

export const getRecentlyAccessedJobs = (): RecentJobEntry[] => readEntries();

const formatTimeAgo = (timestamp: number): string => {
  const diffMs = Date.now() - timestamp;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

export const formatRecentJobTime = (timestamp: number): string => formatTimeAgo(timestamp);
